import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { supabaseManager } from '@/services/supabaseManager';
import { simpleUsersService } from '@/services/simpleUsers/simpleUsersService';
import { isAtFastSwitchPath, navigateTo, navigateToAuth } from '@/utils/navigationUtils';
import { clearAuthKeys } from '@/utils/storage';
import { isDeviceOffline } from '@/lib/onlineStatus';
import {
  getCachedOrganizationCredentials,
  readStoredSessionForUrl,
} from '@/services/supabase/utils/credentialsCache';
import { simpleUserFromOperator } from './auth.operator';
import type { AuthStoreGet, AuthStoreSet } from './auth.types';

/**
 * Offline cold-start fallback: trust the session gotrue already persisted for
 * the org project. `getSession()` can come back empty offline when the access
 * token is expired (refreshing needs the network), but the stored session and
 * its refresh token are still good — gotrue auto-refreshes once connectivity
 * returns and `TOKEN_REFRESHED` updates the store. Never used while online, so
 * a genuinely revoked session still signs out as before.
 */
async function readOfflineSessionFallback(slug: string): Promise<Session | null> {
  if (!(await isDeviceOffline())) return null;
  const credentials = getCachedOrganizationCredentials(slug);
  if (!credentials) return null;
  return readStoredSessionForUrl(credentials.supabaseUrl);
}

function redirectToFastSwitchIfWaiting(get: AuthStoreGet): void {
  if (typeof window === 'undefined') return;
  if (!get().isInWaitingScreen) return;
  if (isAtFastSwitchPath()) return;

  navigateTo('/fast-switch');
}

function sendToFastSwitchPicker(set: AuthStoreSet, get: AuthStoreGet): void {
  get().clearActiveOperator();
  set({ isInWaitingScreen: true });
  if (!isAtFastSwitchPath()) {
    navigateTo('/fast-switch');
  }
}

export const createLifecycleActions = (set: AuthStoreSet, get: AuthStoreGet) => ({
  signOut: async (): Promise<{ error: Error | null }> => {
    const { useOrganizationStore } = await import('../organizationStore');
    const orgSlug =
      useOrganizationStore.getState().currentOrganizationSlug ??
      localStorage.getItem('currentOrganizationSlug');

    try {
      const client = supabaseManager.getCurrentClient();
      await client.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
    get().clearAuthState();
    supabaseManager.disconnect();
    clearAuthKeys();

    const slug = orgSlug?.trim() || null;
    if (slug) {
      localStorage.setItem('currentOrganizationSlug', slug);
    }
    navigateToAuth();
    return { error: null };
  },

  initializeAuth: async () => {
    const storedSlug = localStorage.getItem('currentOrganizationSlug');

    try {
      set({ loading: true });
      const resolveValidatedSession = async (): Promise<Session | null> => {
        if (storedSlug) {
          try {
            await supabaseManager.connectToOrganizationBySlug(storedSlug);
            const organizationClient = supabaseManager.getCurrentClient();
            const {
              data: { session },
            } = await organizationClient.auth.getSession();
            if (session) return session;

            const { data: refreshed, error: refreshError } =
              await organizationClient.auth.refreshSession();
            if (refreshError) throw refreshError;
            if (refreshed.session) return refreshed.session;
          } catch (error) {
            console.log('Error validating organization session:', error);
          }

          // Reached only when the online path produced no session (connect
          // failed or the token couldn't be refreshed). Offline, fall back to
          // the locally stored session so the POS can boot without network.
          const offlineSession = await readOfflineSessionFallback(storedSlug);
          if (offlineSession) {
            console.warn('[Auth] Offline cold start: using locally stored session');
            return offlineSession;
          }
        }

        const mainClient = supabaseManager.getMainClient();
        const {
          data: { session: mainSession },
        } = await mainClient.auth.getSession();
        if (mainSession) return mainSession;

        const { data: refreshedMain, error: refreshMainError } =
          await mainClient.auth.refreshSession();
        if (refreshMainError) throw refreshMainError;
        return refreshedMain.session ?? null;
      };

      const session = await resolveValidatedSession();

      if (session) {
        set({
          session,
          user: session.user,
          isAuthenticated: true,
        });

        const operator = get().activeOperator;

        if (!operator) {
          sendToFastSwitchPicker(set, get);
        } else if (operator.type === 'member') {
          get().applyMemberOperator();
          await get().fetchUserProfile(session.user.id);
          redirectToFastSwitchIfWaiting(get);
        } else {
          // Offline, the validation query cannot run; trust the persisted
          // operator so the device isn't locked out of the POS. Deactivations
          // are enforced again on the next online start. (Re-picking at
          // /fast-switch wouldn't help offline anyway — PIN sign-in needs the
          // network.)
          const restoreOperatorOffline = () => {
            const persistedSimpleUser = simpleUserFromOperator(operator);
            if (persistedSimpleUser) {
              console.warn('[Auth] Offline cold start: restoring persisted operator');
              get().applySimpleUserOperator(persistedSimpleUser);
              redirectToFastSwitchIfWaiting(get);
              return true;
            }
            return false;
          };

          try {
            const validated = await simpleUsersService.getActiveSimpleUser(operator.id);
            if (!validated) {
              sendToFastSwitchPicker(set, get);
            } else {
              get().applySimpleUserOperator(validated);
              redirectToFastSwitchIfWaiting(get);
            }
          } catch (error) {
            console.error('[Auth] Failed to restore simple-user operator:', error);
            if (!(await isDeviceOffline()) || !restoreOperatorOffline()) {
              sendToFastSwitchPicker(set, get);
            }
          }
        }
      } else {
        get().clearAuthState();
      }
    } catch (error) {
      console.error('[Auth] initializeAuth failed while validating session:', error);
    } finally {
      set({ loading: false });
    }
  },

  handleAuthStateChange: async (event: AuthChangeEvent, session: Session | null) => {
    const currentSession = get().session;

    if (session?.access_token === currentSession?.access_token && event !== 'SIGNED_OUT') {
      return;
    }

    switch (event) {
      case 'SIGNED_IN':
      case 'TOKEN_REFRESHED':
        if (session) {
          set({
            session,
            user: session.user,
            isAuthenticated: true,
            loading: false,
          });
        }
        break;

      case 'SIGNED_OUT':
        get().clearAuthState();
        break;
    }
  },
});
